import React, { useState, useEffect } from 'react';
import MfaSetupQrCode from '../components/MfaSetupQrCode';
import OtpValidate from '../components/OtpValidate';
import CustomPopup from '../components/BtAdminPanel/CustomPopup';
import UserLogin from '../utils/UserLogin/UserLogin';
import { UserType } from '../utils/Helper/enums';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { adminRoutes, btAdminRoutes, routes, userRoutes } from '../routes/Routes';
import AuthLayout from '../layout/AuthLayout';
import { useAppDispatch } from '../hooks/storeHooks';
import { updateUserData } from '../store/slice/sessionSlice';
import { encrypt } from '../utils/Helper/encryption';
import BtSmLogo from '../library/Logo/BtSmLogo';

const Mfa: React.FC = () => {
  const dispatch = useAppDispatch();
  const [secretCode, setSecretCode] = useState(sessionStorage.getItem('secretCode'));
  const [accessToken, setAccessToken] = useState(sessionStorage.getItem('accessToken') ?? '');
  const [isMfaSetupDone, setIsMfaSetupDone] = useState<boolean>(
    sessionStorage.getItem('isMfaSetup') === 'true'
  );
  const [email, setEmail] = useState(sessionStorage.getItem('userEmail'));
  const [userPass, setUserPass] = useState(sessionStorage.getItem('userPassword') ?? '');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showMfaSuccessPopup, setShowMfaSuccessPopup] = useState<boolean>(false);
  const [popupMessage, setPopupMessage] = useState<string>('');
  const [showErrorPopup, setShowErrorPopup] = useState<boolean>(false);

  const showError = (message: string) => {
    setPopupMessage(message);
    setShowErrorPopup(true);
  };

  const navigate: NavigateFunction = useNavigate();

  useEffect(() => {
    if (!sessionStorage.getItem('userEmail')) {
      void navigate(routes.login);
    }
  }, []);

  return (
    <AuthLayout>
      <div className="min-w-[288px] sm:w-[20rem] md:w-96 max-w-[28rem] block rounded-lg shadow-lg bg-white p-4 py-6 md:p-8">
        <BtSmLogo />
        <CustomPopup
          show={showMfaSuccessPopup}
          onClose={() => {
            setShowMfaSuccessPopup(false);
            setIsMfaSetupDone(true);
          }}
          width="w-80"
        >
          <p className="text-center text-sm font-medium">Your MFA setup is successfull.</p>
        </CustomPopup>
        <CustomPopup
          show={showErrorPopup}
          onClose={() => setShowErrorPopup(false)}
          width="w-80"
        >
          <p className="text-center text-sm font-medium">{popupMessage}</p>
        </CustomPopup>
        {isMfaSetupDone ? (
          <OtpValidate
            isLoading={isLoading}
            onOtpSubmission={async (code: string) => {
              setIsLoading(true);
              const userLoginObj = new UserLogin({
                email: email ?? '',
                password: userPass ?? ''
              });
              const loginStatus = await userLoginObj.doLogin(code);
              const responseData: any = loginStatus?.body ?? {};
              const userType = responseData?.userType ?? Number.MAX_SAFE_INTEGER;
              switch (loginStatus.statusCode) {
                case 201:
                  switch (userType) {
                    case UserType.BtAdmin:
                      setIsLoading(false);
                      void navigate(btAdminRoutes.partnerAdmin);
                      break;
                    case UserType.OrganizationAdmin:
                      setIsLoading(false);
                      sessionStorage.setItem('loginToken', encrypt(JSON.stringify(responseData)));
                      dispatch(updateUserData(JSON.stringify(responseData)));
                      void navigate(adminRoutes.dashoard);
                      break;
                    case UserType.OrganizationUser:
                      setIsLoading(false);
                      sessionStorage.setItem('loginToken', encrypt(JSON.stringify(responseData)));
                      dispatch(updateUserData(JSON.stringify(responseData)));
                      void navigate(userRoutes.dashboard);
                      break;
                    default:
                      break;
                  }
                  break;
                case 410:
                  setIsLoading(false);
                  showError('Sorry OTP mismatched');
                  break;
                case 401:
                  setIsLoading(false);
                  showError('Invalid credentials.');
                  void navigate(routes.login);
                  break;
                case 403:
                  setIsLoading(false);
                  showError('Sorry something went wrong, please try again later.');
                  break;
                case 500:
                  setIsLoading(false);
                  showError('Sorry something went wrong, please try again later.');
                  break;
                default:
              }
              // Storing the login token and email address of the user
              sessionStorage.setItem('user-type', userType);
            }}
          />
        ) : (
          <MfaSetupQrCode
            isLoading={isLoading}
            secretCode={secretCode ?? ''}
            onOtpVerification={async (code: string) => {
              setIsLoading(true);
              const userLoginObj = new UserLogin({
                email: email ?? '',
                password: userPass ?? ''
              });
              const loginStatus = await userLoginObj.userMfaSetup(code, accessToken);
              const responseData: any = loginStatus?.body ?? {};
              if (loginStatus.statusCode === 201) {
                setIsLoading(false);
                setShowMfaSuccessPopup(true);
              } else {
                setIsLoading(false);
                showError('Sorry something went wrong, please try again later.');
              }
            }}
          />
        )}
      </div>
    </AuthLayout>
  );
};

export default Mfa;
